import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Download, Upload, Calendar, Settings, Users, ShieldOff, LogOut, ArrowLeft } from 'lucide-react';
import GlowBackground from '../components/GlowBackground';
import Logo from '../components/Logo';
import AuthModal from '../components/AuthModal';
import { Toaster } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ThoughtsTab from './admin/ThoughtsTab';
import DailySchedulerTab from './admin/DailySchedulerTab';
import TypesTab from './admin/TypesTab';
import UsersTab from './admin/UsersTab';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [activeTab, setActiveTab] = useState('thoughts');
  const thoughtsTabRef = useRef(null);

  // ── Loading state ──
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
        <div className="w-10 h-10 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
      </div>
    );
  }

  // ── Not signed in ──
  if (!user) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
        <GlowBackground />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-8 rounded-2xl shadow-xl relative z-10 text-center"
          style={{ backgroundColor: 'rgba(255, 252, 248, 0.95)' }}
        >
          <div className="flex justify-center mb-6"><Logo size="default" /></div>
          <h2 className="font-serif text-2xl text-charcoal mb-2">Admin Panel</h2>
          <p className="text-charcoal/60 mb-8">Sign in to access the admin panel.</p>
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => setShowAuthModal(true)}
            className="w-full py-3 rounded-full font-medium text-white mb-4"
            style={{ background: '#ff780a' }}
            data-testid="admin-signin-btn"
          >
            Sign in with Magic Link
          </motion.button>
          <button onClick={() => navigate('/')} className="text-charcoal/50 hover:text-charcoal text-sm transition-colors flex items-center gap-1 mx-auto">
            <ArrowLeft size={14} /> Back to home
          </button>
        </motion.div>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
        <Toaster position="top-center" />
      </div>
    );
  }

  // ── Signed in but not admin ──
  if (!user.is_admin) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
        <GlowBackground />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-8 rounded-2xl shadow-xl relative z-10 text-center"
          style={{ backgroundColor: 'rgba(255, 252, 248, 0.95)' }}
        >
          <div className="flex justify-center mb-6"><Logo size="default" /></div>
          <ShieldOff size={40} className="text-charcoal/30 mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-charcoal mb-2">Not Authorized</h2>
          <p className="text-charcoal/60 mb-2">You're signed in as <strong>{user.email}</strong></p>
          <p className="text-charcoal/50 text-sm mb-8">This account doesn't have admin access. Ask an existing admin to grant you access.</p>
          <button onClick={() => navigate('/')} className="text-charcoal/50 hover:text-charcoal text-sm transition-colors flex items-center gap-1 mx-auto">
            <ArrowLeft size={14} /> Back to home
          </button>
        </motion.div>
        <Toaster position="top-center" />
      </div>
    );
  }

  // ── Admin Dashboard ──
  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 border-b border-charcoal/10 px-6 py-4"
        style={{ backgroundColor: 'rgba(255, 248, 240, 0.95)', backdropFilter: 'blur(8px)' }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')}><Logo size="small" /></button>
            <span className="text-charcoal/40">|</span>
            <span className="font-medium text-charcoal">Admin Panel</span>
            <span className="text-xs text-charcoal/40 hidden sm:block">{user.email}</span>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'thoughts' && (
              <>
                <button
                  onClick={() => thoughtsTabRef.current?.exportCSV()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-charcoal/10 hover:bg-charcoal/5 transition-colors"
                  data-testid="export-button"
                >
                  <Download size={16} /><span className="hidden sm:inline">Export CSV</span>
                </button>
                <button
                  onClick={() => thoughtsTabRef.current?.openImport()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-charcoal/10 hover:bg-charcoal/5 transition-colors"
                  data-testid="import-button"
                >
                  <Upload size={16} /><span className="hidden sm:inline">Import CSV</span>
                </button>
                <button
                  onClick={() => thoughtsTabRef.current?.openAdd()}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-white"
                  style={{ background: '#ff780a' }}
                  data-testid="add-thought-button"
                >
                  <Plus size={16} /><span className="hidden sm:inline">Add Thought</span>
                </button>
              </>
            )}
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="flex items-center gap-1.5 px-3 py-2 text-charcoal/60 hover:text-charcoal transition-colors text-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="max-w-7xl mx-auto flex gap-1 mt-3 flex-wrap">
          {[
            { id: 'thoughts', label: 'Question Bank', icon: null },
            { id: 'daily', label: 'Daily Thought', icon: Calendar },
            { id: 'types', label: 'Types', icon: Settings },
            { id: 'users', label: 'Users', icon: Users },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                activeTab === id ? 'bg-orange-primary/10 text-orange-primary' : 'text-charcoal/50 hover:text-charcoal hover:bg-charcoal/5'
              }`}
              data-testid={`tab-${id}`}
            >
              {Icon && <Icon size={14} />}{label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'thoughts' && <ThoughtsTab ref={thoughtsTabRef} />}
        {activeTab === 'daily' && <DailySchedulerTab />}
        {activeTab === 'types' && <TypesTab />}
        {activeTab === 'users' && <UsersTab />}
      </main>

      <Toaster position="top-center" toastOptions={{
        style: { background: '#FFF8F0', border: '1px solid rgba(255, 120, 10, 0.2)', color: '#2C2417' },
      }} />
    </div>
  );
};

export default AdminPanel;
